import { useCallback, useRef, useState } from "react";

/**
 * Manages the open/close animation state for an expandable activity card.
 *
 * `animateExpand` drives the CSS animation class:
 *   true  → rootAnimation (expand keyframe)
 *   false → root (collapse keyframe)
 *
 * Collapse uses a 650 ms delay so the closing animation can finish before
 * React removes the expanded content from the DOM.
 *
 * `toggle()` returns `true` when expanding, `false` when collapsing,
 * so callers can run side-effects only on expand.
 */
export function useActivityExpand() {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [animateExpand, setAnimateExpand] = useState(false);
  const collapseTimer = useRef(null);

  const open = useCallback(() => {
    if (collapseTimer.current) {
      clearTimeout(collapseTimer.current);
      collapseTimer.current = null;
    }
    setAnimateExpand(true);
    setDetailsOpen(true);
  }, []);

  const close = useCallback(() => {
    setAnimateExpand(false);
    if (collapseTimer.current) {
      clearTimeout(collapseTimer.current);
    }
    collapseTimer.current = setTimeout(() => {
      collapseTimer.current = null;
      setDetailsOpen(false);
    }, 650);
  }, []);

  /** @returns {boolean} `true` if expanding, `false` if collapsing */
  const toggle = useCallback(() => {
    if (detailsOpen) {
      close();
      return false;
    }
    open();
    return true;
  }, [detailsOpen, open, close]);

  return { detailsOpen, animateExpand, open, close, toggle };
}
